/**
 * useContentRuleCenter Hook
 * Custom hook for managing Content Rule Center state and data fetching
 */
import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useAtom } from 'jotai';
import { contentRuleCenterStateSegment } from '../store/globalStore/stateSegments/contentRuleCenterStateSegment';
import {
  initializeContentRuleCenter,
  refreshContentRuleCenter,
  updateContentRuleCenterFilters,
  updateContentRuleCenterFiltersBatch,
  resetContentRuleCenterFilters,
  fetchCategorySummaries,
  fetchCategoryRules,
} from '../store/globalStore/actions/contentRuleCenterActions';
import { ContentRuleCenterSelectedFilters } from '../store/globalStore/stateSegments/contentRuleCenterStateSegment';
import {
  RuleItem,
  RuleCategory,
} from '../components/widgets/ContentRuleCenter';
import { RuleSeverity, SeverityCode } from '../types/ContentRules';

/**
 * Maps API severity to component severity
 */
const mapSeverity = (apiSeverity: RuleSeverity): RuleItem['severity'] => {
  switch (apiSeverity) {
    case SeverityCode.BLOCKER:
      return SeverityCode.BLOCKER;
    case SeverityCode.WARNING:
      return SeverityCode.WARNING;
    case SeverityCode.INFO:
      return SeverityCode.INFO;
    default:
      return SeverityCode.INFO;
  }
};

/**
 * Custom hook for Content Rule Center
 * Provides state, data, and handlers for the ContentRuleCenter component
 */
export const useContentRuleCenter = () => {
  const [state] = useAtom(contentRuleCenterStateSegment);
  const initializationStartedRef = useRef(false);

  // Initialize data when hook mounts (only if not already initialized)
  useEffect(() => {
    if (
      !state.isInitialized &&
      !state.isInitialLoading &&
      !initializationStartedRef.current
    ) {
      initializationStartedRef.current = true;
      initializeContentRuleCenter();
    }
  }, [state.isInitialized, state.isInitialLoading]);

  /**
   * Handles filter changes from the component
   * Only refetches category summaries (not rules - those are fetched on accordion open)
   */
  const handleFilterChange = useCallback(
    (filterType: keyof ContentRuleCenterSelectedFilters) => {
      return (value: string | string[]) => {
        const values = Array.isArray(value) ? value : [value];
        updateContentRuleCenterFilters(filterType, values);

        // Refetch category summaries with new filters
        fetchCategorySummaries();
      };
    },
    []
  );

  /**
   * Handles batch filter changes from the component
   * Updates all filters at once and triggers only ONE API call
   */
  const handleFiltersBatchChange = useCallback(
    (filters: Partial<ContentRuleCenterSelectedFilters>) => {
      updateContentRuleCenterFiltersBatch(filters);
    },
    []
  );

  /**
   * Handles reset filters action
   */
  const handleResetFilters = useCallback(() => {
    resetContentRuleCenterFilters();
    fetchCategorySummaries();
  }, []);

  /**
   * Handles accordion open - fetches rules for that category
   */
  const handleAccordionOpen = useCallback(
    (categoryId: string) => {
      // Check if rules are already fetched for this category
      const categoryRulesState = state.categoryRules[categoryId];
      if (!categoryRulesState?.isFetched) {
        fetchCategoryRules(categoryId, categoryId, 1, 20, false);
      }
    },
    [state.categoryRules]
  );

  /**
   * Handles load more rules for infinite scroll
   */
  const handleLoadMoreRules = useCallback(
    (categoryId: string) => {
      const categoryRulesState = state.categoryRules[categoryId];
      if (categoryRulesState && !categoryRulesState.loading) {
        const nextPage = categoryRulesState.pagination.page + 1;
        if (nextPage <= categoryRulesState.pagination.totalPages) {
          fetchCategoryRules(categoryId, categoryId, nextPage, 20, true);
        }
      }
    },
    [state.categoryRules]
  );

  /**
   * Handles retry action on error
   */
  const handleRetry = useCallback(() => {
    refreshContentRuleCenter();
  }, []);

  /**
   * Maps severity summary to quality card counts
   */
  const severityCounts = useMemo(() => {
    const counts = {
      allRulesCount: parseInt(state.severitySummary.totalRuleCount) || 0,
      mustComplyCount: 0,
      shouldComplyCount: 0,
      goodToHaveCount: 0,
    };

    state.severitySummary.severityRuleCounts.forEach(item => {
      switch (item.severityCode) {
        case SeverityCode.BLOCKER:
          counts.mustComplyCount = item.ruleCount;
          break;
        case SeverityCode.WARNING:
          counts.shouldComplyCount = item.ruleCount;
          break;
        case SeverityCode.INFO:
          counts.goodToHaveCount = item.ruleCount;
          break;
        default:
          break;
      }
    });

    return counts;
  }, [state.severitySummary]);

  /**
   * Maps category summary to component format with rules
   */
  const categories: RuleCategory[] = useMemo(() => {
    // Get category metadata from filterOptions

    return state.categorySummary.categoryRuleCounts.map(categoryCount => {
      // Calculate severity counts from severityRuleCounts
      let mustComply = 0;
      let shouldComply = 0;
      let goodToHave = 0;

      categoryCount.severityRuleCounts.forEach(severity => {
        switch (severity.severityCode) {
          case SeverityCode.BLOCKER:
            mustComply = severity.ruleCount;
            break;
          case SeverityCode.WARNING:
            shouldComply = severity.ruleCount;
            break;
          case SeverityCode.INFO:
            goodToHave = severity.ruleCount;
            break;
          default:
            break;
        }
      });

      // Get per-category rules state
      const categoryRulesState =
        state.categoryRules[categoryCount.categoryCode];

      // Map rules if available
      const categoryRules: RuleItem[] = categoryRulesState?.rules
        ? categoryRulesState.rules.map(rule => ({
            id: rule.id,
            name: rule.name,
            description: rule.description,
            tags: rule.tags,
            severity: mapSeverity(rule.severity.code as RuleSeverity),
            lastUpdated: rule.lastUpdatedDate,
          }))
        : [];

      return {
        id: categoryCount.categoryCode,
        name: categoryCount.categoryName,
        mustComplyCount: mustComply,
        shouldComplyCount: shouldComply,
        goodToHaveCount: goodToHave,
        rules: categoryRules,
        // Include per-category loading state
        isLoading: categoryRulesState?.loading || false,
        isFetched: categoryRulesState?.isFetched || false,
        pagination: categoryRulesState?.pagination,
        error: categoryRulesState?.error,
      };
    });
  }, [state.categorySummary.categoryRuleCounts, state.categoryRules]);

  /**
   * Filter options formatted for CapsuleDropdown
   */
  const filterOptions = useMemo(() => {
    return {
      countries: [...state.filterOptions.countriesOptions.options],
      brands: state.filterOptions.brandsOptions.options.map(brand => ({
        ...brand,
        label: brand.label.toUpperCase(),
      })),
      audiences: [...state.filterOptions.audiencesOptions.options],
      channels: [...state.filterOptions.channelsOptions.options],
      categoryFilters: state.filterOptions.categoriesOptions.options.map(
        cat => cat.name
      ),
    };
  }, [state.filterOptions]);

  /**
   * Loading state combining initial load and individual section loads
   */
  const isLoading = useMemo(() => {
    return state.isInitialLoading;
  }, [state.isInitialLoading]);

  /**
   * Table loading state (for showing loader in table area)
   */
  const isTableLoading = useMemo(() => {
    return state.isTableLoading || state.categorySummary.loading;
  }, [state.isTableLoading, state.categorySummary.loading]);

  /**
   * Whether filters/dropdowns and quality cards are loaded
   */
  const areFiltersLoaded = useMemo(() => {
    return state.areFiltersLoaded;
  }, [state.areFiltersLoaded]);

  /**
   * Combined error state
   */
  const error = useMemo(() => {
    return (
      state.error ||
      state.severitySummary.error ||
      state.categorySummary.error ||
      state.contentRules.error
    );
  }, [
    state.error,
    state.severitySummary.error,
    state.categorySummary.error,
    state.contentRules.error,
  ]);

  return {
    // Loading States
    isLoading,
    isTableLoading,
    areFiltersLoaded,
    error,
    isInitialized: state.isInitialized,
    selectedFilters: state.selectedFilters,

    // Data
    ...severityCounts,
    categories,
    ...filterOptions,

    // Handlers
    handleFilterChange,
    handleFiltersBatchChange,
    handleResetFilters,
    handleRetry,
    handleAccordionOpen,
    handleLoadMoreRules,

    // Raw state (for advanced use cases)
    rawState: state,
  };
};

export default useContentRuleCenter;
